import { CommentType, DocumentType, EquipmentGroup, LinkType } from '~/db_types/swagger_types';
import { UserPermission } from '~/nasa_ui/types/enums';
const BASE_PERMISSIONS = [
    UserPermission.APPROVAL_ROUTING_TEMPLATE_ADMIN,
    UserPermission.CHANGE_SET_DELETE,
    UserPermission.CONTACT_ADMIN,
    UserPermission.DOCUMENT_ADMIN,
    UserPermission.HARDWARE_LIST_ADMIN,
    UserPermission.LOCATION_ADMIN,
    UserPermission.USER_ADMIN,
    UserPermission.USER_GROUPS_ADMIN,
    UserPermission.USER_PERMISSIONS,
    UserPermission.USER_SUPPORT_CONTEXT
];
const BASE_COMMENT_TYPES = [CommentType.GENERAL, CommentType.NOTE];
export const isCosmicDocumentType = (dt) => dt.startsWith('C_');
export const isTrackingDocumentType = (dt) => !isCosmicDocumentType(dt);
const withBasePermissions = (...extras) => [...BASE_PERMISSIONS, ...extras];
const withBaseCommentTypes = (...extras) => [...BASE_COMMENT_TYPES, ...extras];
// a SYSTEM has CosmicUserSupportContexts
export var CosmicSystem;
(function (CosmicSystem) {
    CosmicSystem["ESOC"] = "ESOC";
})(CosmicSystem || (CosmicSystem = {}));
// This is a list of every possible valid user support context within any application in the cosmic realm
export var CosmicUserSupportContext;
(function (CosmicUserSupportContext) {
    CosmicUserSupportContext["CTSD"] = "CTSD";
    CosmicUserSupportContext["EHP"] = "EHP";
    CosmicUserSupportContext["ER"] = "ER";
    CosmicUserSupportContext["ES"] = "ES";
    CosmicUserSupportContext["ESOC"] = "ESOC";
    CosmicUserSupportContext["EV"] = "EV";
    CosmicUserSupportContext["JETS_II"] = "JETS_II";
    CosmicUserSupportContext["JSC_CTX"] = "JSC_CTX";
    CosmicUserSupportContext["NASA"] = "NASA";
    CosmicUserSupportContext["NMC"] = "NMC";
    CosmicUserSupportContext["SEAL"] = "SEAL";
    CosmicUserSupportContext["xEMU"] = "xEMU";
    CosmicUserSupportContext["XLABS"] = "XLABS";
})(CosmicUserSupportContext || (CosmicUserSupportContext = {}));
export const CosmicUserSupportContextDisplay = new Map([
    [CosmicUserSupportContext.CTSD, 'CTSD'],
    [CosmicUserSupportContext.EHP, 'EHP'],
    [CosmicUserSupportContext.ER, 'ER'],
    [CosmicUserSupportContext.ES, 'ES'],
    [CosmicUserSupportContext.ESOC, 'ESOC'],
    [CosmicUserSupportContext.EV, 'EV'],
    [CosmicUserSupportContext.JSC_CTX, 'JSC'],
    [CosmicUserSupportContext.JETS_II, 'JETS II'],
    [CosmicUserSupportContext.NASA, 'NASA'],
    [CosmicUserSupportContext.NMC, 'NASA Mission Cloud'],
    [CosmicUserSupportContext.SEAL, 'Surface Exploration Avionics Laboratory'],
    [CosmicUserSupportContext.xEMU, 'xEMU'],
    [CosmicUserSupportContext.XLABS, 'X-Labs']
]);
// an APPLICATION has CosmicUserSupportContexts(sub-set of the SYSTEM)
export var EsocApplication;
(function (EsocApplication) {
    EsocApplication["CDOCS"] = "CDOCS";
    EsocApplication["KNOWLEDGE_BASE"] = "KNOWLEDGE_BASE";
    EsocApplication["LOGISTICS"] = "LOGISTICS";
    EsocApplication["OG"] = "OG";
    EsocApplication["RISK"] = "RISK";
})(EsocApplication || (EsocApplication = {}));
export const EsocApplicationDisplay = new Map([
    [EsocApplication.CDOCS, 'cDocs'],
    [EsocApplication.KNOWLEDGE_BASE, 'Knowledge base'],
    [EsocApplication.LOGISTICS, 'Logistics'],
    [EsocApplication.OG, 'COSMIC'],
    [EsocApplication.RISK, 'Risk']
]);
const ALL_EQUIPMENT_GROUPS = Object.values(EquipmentGroup);
const DOC_TYPE_TPS_DR = [DocumentType.C_TPS, DocumentType.C_NONCONFORMANCE_REPORT, DocumentType.C_PROCEDURE];
const LINK_TYPE_OTHER = [LinkType.OTHER];
const CDOCS_CONTEXTS = [
    CosmicUserSupportContext.CTSD,
    CosmicUserSupportContext.EHP,
    CosmicUserSupportContext.ER,
    CosmicUserSupportContext.ES,
    CosmicUserSupportContext.ESOC,
    CosmicUserSupportContext.EV,
    CosmicUserSupportContext.JETS_II,
    CosmicUserSupportContext.JSC_CTX,
    CosmicUserSupportContext.NASA,
    CosmicUserSupportContext.SEAL,
    CosmicUserSupportContext.xEMU,
    CosmicUserSupportContext.XLABS
];
const CDOCS_APPLICATION_CONFIG = {
    commentTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: withBaseCommentTypes(),
        [CosmicUserSupportContext.EHP]: withBaseCommentTypes(),
        [CosmicUserSupportContext.ER]: withBaseCommentTypes(),
        [CosmicUserSupportContext.ES]: withBaseCommentTypes(),
        [CosmicUserSupportContext.ESOC]: withBaseCommentTypes(),
        [CosmicUserSupportContext.EV]: withBaseCommentTypes(),
        [CosmicUserSupportContext.JETS_II]: withBaseCommentTypes(),
        [CosmicUserSupportContext.JSC_CTX]: withBaseCommentTypes(),
        [CosmicUserSupportContext.NASA]: withBaseCommentTypes(),
        [CosmicUserSupportContext.SEAL]: withBaseCommentTypes(),
        [CosmicUserSupportContext.xEMU]: withBaseCommentTypes(),
        [CosmicUserSupportContext.XLABS]: withBaseCommentTypes()
    },
    documentTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.EHP]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.ER]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.ES]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.ESOC]: [DocumentType.C_WORKFLOW, DocumentType.C_RCA],
        [CosmicUserSupportContext.EV]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.JETS_II]: [...DOC_TYPE_TPS_DR, DocumentType.C_WORKFLOW],
        [CosmicUserSupportContext.JSC_CTX]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.NASA]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.SEAL]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.xEMU]: DOC_TYPE_TPS_DR,
        [CosmicUserSupportContext.XLABS]: DOC_TYPE_TPS_DR
    },
    equipmentGroupsPerCtx: {
        [CosmicUserSupportContext.CTSD]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.EHP]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ER]: [EquipmentGroup.ELECTRICAL, EquipmentGroup.MECHANICAL],
        [CosmicUserSupportContext.ES]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ESOC]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.EV]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.JETS_II]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.JSC_CTX]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.NASA]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.SEAL]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.xEMU]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.XLABS]: ALL_EQUIPMENT_GROUPS
    },
    linkTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.EHP]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ER]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ES]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ESOC]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.EV]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.JETS_II]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.JSC_CTX]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.NASA]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.SEAL]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.xEMU]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.XLABS]: LINK_TYPE_OTHER
    },
    permissionsPerCtx: {
        [CosmicUserSupportContext.CTSD]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.EHP]: withBasePermissions(),
        [CosmicUserSupportContext.ER]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.ES]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.ESOC]: withBasePermissions(UserPermission.C_DOCS_WORKFLOWS_ADMIN, UserPermission.C_DOCS_WORKFLOWS_SUBMITTER, UserPermission.C_DOCS_RCA_ADMIN, UserPermission.C_DOCS_RCA_SUBMITTER),
        [CosmicUserSupportContext.EV]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.JETS_II]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_WORKFLOWS_ADMIN, UserPermission.C_DOCS_WORKFLOWS_SUBMITTER),
        [CosmicUserSupportContext.JSC_CTX]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.NASA]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.SEAL]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.xEMU]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN),
        [CosmicUserSupportContext.XLABS]: withBasePermissions(UserPermission.C_DOCS_TPS_ADMIN, UserPermission.C_DOCS_DR_ADMIN, UserPermission.C_DOCS_PROC_ADMIN)
    },
    userSupportContexts: CDOCS_CONTEXTS
};
const KNOWLEDGE_BASE_CONTEXTS = [
    CosmicUserSupportContext.CTSD,
    CosmicUserSupportContext.EHP,
    CosmicUserSupportContext.ER,
    CosmicUserSupportContext.ES,
    CosmicUserSupportContext.ESOC,
    CosmicUserSupportContext.EV,
    CosmicUserSupportContext.JETS_II,
    CosmicUserSupportContext.JSC_CTX,
    CosmicUserSupportContext.NASA,
    CosmicUserSupportContext.NMC,
    CosmicUserSupportContext.SEAL,
    CosmicUserSupportContext.xEMU,
    CosmicUserSupportContext.XLABS
];
const KNOWLEDGE_BASE_APPLICATION_CONFIG = {
    commentTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: [],
        [CosmicUserSupportContext.EHP]: [],
        [CosmicUserSupportContext.ER]: [],
        [CosmicUserSupportContext.ES]: [],
        [CosmicUserSupportContext.ESOC]: [],
        [CosmicUserSupportContext.EV]: [],
        [CosmicUserSupportContext.JETS_II]: [],
        [CosmicUserSupportContext.JSC_CTX]: [],
        [CosmicUserSupportContext.NASA]: [],
        [CosmicUserSupportContext.NMC]: [],
        [CosmicUserSupportContext.SEAL]: [],
        [CosmicUserSupportContext.xEMU]: [],
        [CosmicUserSupportContext.XLABS]: []
    },
    documentTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: [],
        [CosmicUserSupportContext.EHP]: [],
        [CosmicUserSupportContext.ER]: [],
        [CosmicUserSupportContext.ES]: [],
        [CosmicUserSupportContext.ESOC]: [],
        [CosmicUserSupportContext.EV]: [],
        [CosmicUserSupportContext.JETS_II]: [],
        [CosmicUserSupportContext.JSC_CTX]: [],
        [CosmicUserSupportContext.NASA]: [],
        [CosmicUserSupportContext.NMC]: [],
        [CosmicUserSupportContext.SEAL]: [],
        [CosmicUserSupportContext.xEMU]: [],
        [CosmicUserSupportContext.XLABS]: []
    },
    equipmentGroupsPerCtx: {
        [CosmicUserSupportContext.CTSD]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.EHP]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ER]: [EquipmentGroup.ELECTRICAL, EquipmentGroup.MECHANICAL],
        [CosmicUserSupportContext.ES]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ESOC]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.EV]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.JETS_II]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.JSC_CTX]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.NASA]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.NMC]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.SEAL]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.xEMU]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.XLABS]: ALL_EQUIPMENT_GROUPS
    },
    linkTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.EHP]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ER]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ES]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ESOC]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.EV]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.JETS_II]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.JSC_CTX]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.NASA]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.NMC]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.SEAL]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.xEMU]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.XLABS]: LINK_TYPE_OTHER
    },
    permissionsPerCtx: {
        [CosmicUserSupportContext.CTSD]: withBasePermissions(),
        [CosmicUserSupportContext.EHP]: withBasePermissions(),
        [CosmicUserSupportContext.ER]: withBasePermissions(),
        [CosmicUserSupportContext.ES]: withBasePermissions(),
        [CosmicUserSupportContext.ESOC]: withBasePermissions(),
        [CosmicUserSupportContext.EV]: withBasePermissions(),
        [CosmicUserSupportContext.JETS_II]: withBasePermissions(),
        [CosmicUserSupportContext.JSC_CTX]: withBasePermissions(),
        [CosmicUserSupportContext.NASA]: withBasePermissions(),
        [CosmicUserSupportContext.NMC]: withBasePermissions(),
        [CosmicUserSupportContext.SEAL]: withBasePermissions(),
        [CosmicUserSupportContext.xEMU]: withBasePermissions(),
        [CosmicUserSupportContext.XLABS]: withBasePermissions()
    },
    userSupportContexts: KNOWLEDGE_BASE_CONTEXTS
};
const LOGISTICS_CONTEXTS = [CosmicUserSupportContext.ESOC];
const LOGISTICS_APPLICATION_CONFIG = {
    commentTypesPerCtx: {
        [CosmicUserSupportContext.ESOC]: withBaseCommentTypes(CommentType.STATUS)
    },
    documentTypesPerCtx: {
        [CosmicUserSupportContext.ESOC]: [DocumentType.DELIVERY_ORDER]
    },
    equipmentGroupsPerCtx: {
        [CosmicUserSupportContext.ESOC]: ALL_EQUIPMENT_GROUPS
    },
    linkTypesPerCtx: {
        [CosmicUserSupportContext.ESOC]: LINK_TYPE_OTHER
    },
    permissionsPerCtx: {
        [CosmicUserSupportContext.ESOC]: withBasePermissions(UserPermission.LOGISTICS_DELIVERY_MATRIX_ITEM_ADMIN, UserPermission.LOGISTICS_DELIVERY_ORDER_ADMIN, UserPermission.LOGISTICS_MEGA_ADMIN, UserPermission.USER_ADMIN, UserPermission.USER_GROUPS_ADMIN, UserPermission.USER_PERMISSIONS)
    },
    userSupportContexts: LOGISTICS_CONTEXTS
};
const OG_CONTEXTS = [
    CosmicUserSupportContext.CTSD,
    CosmicUserSupportContext.EHP,
    CosmicUserSupportContext.ER,
    CosmicUserSupportContext.ES,
    CosmicUserSupportContext.ESOC,
    CosmicUserSupportContext.EV,
    CosmicUserSupportContext.JETS_II,
    CosmicUserSupportContext.JSC_CTX,
    CosmicUserSupportContext.NASA,
    CosmicUserSupportContext.SEAL,
    CosmicUserSupportContext.xEMU,
    CosmicUserSupportContext.XLABS
];
const ALL_OG_PERMISSIONS = [
    UserPermission.ACTIVITY_ADMIN,
    UserPermission.APPROVAL_ROUTING_TEMPLATE_ADMIN,
    UserPermission.ASSEMBLY_ADMIN,
    UserPermission.ASSEMBLY_TEMPLATE_ADMIN,
    UserPermission.CHANGE_SET_DELETE,
    UserPermission.CHANGE_SET_TEMPLATE_ADMIN,
    UserPermission.CONTACT_ADMIN,
    UserPermission.CONTRACT_ADMIN,
    UserPermission.DATA_REMEDIATION_ADMIN,
    UserPermission.DOCUMENT_ADMIN,
    UserPermission.EVENT_ADMIN,
    UserPermission.HARDWARE_LIST_ADMIN,
    UserPermission.HARDWARE_LIST_TEMPLATE_ADMIN,
    UserPermission.ITEM_DRAWING_ADMIN,
    UserPermission.ITEM_DRAWING_ALIAS,
    UserPermission.ITEM_DRAWING_CODES,
    UserPermission.ITEM_DRAWING_VALID_SIZES,
    UserPermission.ITEM_INSTANCE_ADMIN,
    UserPermission.ITEM_INSTANCE_DATES,
    UserPermission.ITEM_INSTANCE_LIMITED_LIFE,
    UserPermission.ITEM_MASTER_ADMIN,
    UserPermission.ITEM_MASTER_ALTERNATES,
    UserPermission.ITEM_MASTER_GROUND_SUPPORT_EQUIPMENT,
    UserPermission.ITEM_MASTER_INVENTORY_QUOTAS_ADMIN,
    UserPermission.ITEM_MASTER_LIMITED_LIFE,
    UserPermission.LOCATION_ADMIN,
    UserPermission.MERGE_ITEM_INSTANCES,
    UserPermission.ON_ORBIT_FAILURE_ADMIN,
    UserPermission.ORGANIZATION_ADMIN,
    UserPermission.PHYSICAL_INVENTORY_AUDIT_ADMIN,
    UserPermission.USER_ADMIN,
    UserPermission.USER_GROUPS_ADMIN,
    UserPermission.USER_PERMISSIONS,
    UserPermission.USER_SUPPORT_CONTEXT
];
const OG_APPLICATION_CONFIG = {
    commentTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: withBaseCommentTypes(),
        [CosmicUserSupportContext.EHP]: withBaseCommentTypes(),
        [CosmicUserSupportContext.ER]: withBaseCommentTypes(),
        [CosmicUserSupportContext.ES]: withBaseCommentTypes(),
        [CosmicUserSupportContext.ESOC]: withBaseCommentTypes(),
        [CosmicUserSupportContext.EV]: withBaseCommentTypes(),
        [CosmicUserSupportContext.JETS_II]: withBaseCommentTypes(),
        [CosmicUserSupportContext.JSC_CTX]: withBaseCommentTypes(),
        [CosmicUserSupportContext.NASA]: withBaseCommentTypes(),
        [CosmicUserSupportContext.SEAL]: withBaseCommentTypes(),
        [CosmicUserSupportContext.xEMU]: withBaseCommentTypes(),
        [CosmicUserSupportContext.XLABS]: withBaseCommentTypes()
    },
    documentTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.EHP]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.ER]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.ES]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.ESOC]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.EV]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.JETS_II]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.JSC_CTX]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.NASA]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.SEAL]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.xEMU]: Object.values(DocumentType).filter(isTrackingDocumentType),
        [CosmicUserSupportContext.XLABS]: Object.values(DocumentType).filter(isTrackingDocumentType)
    },
    equipmentGroupsPerCtx: {
        [CosmicUserSupportContext.CTSD]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.EHP]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ER]: [EquipmentGroup.ELECTRICAL, EquipmentGroup.MECHANICAL],
        [CosmicUserSupportContext.ES]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ESOC]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.EV]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.JETS_II]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.JSC_CTX]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.NASA]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.SEAL]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.xEMU]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.XLABS]: ALL_EQUIPMENT_GROUPS
    },
    linkTypesPerCtx: {
        [CosmicUserSupportContext.CTSD]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.EHP]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ER]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ES]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ESOC]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.EV]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.JETS_II]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.JSC_CTX]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.NASA]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.SEAL]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.xEMU]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.XLABS]: LINK_TYPE_OTHER
    },
    permissionsPerCtx: {
        [CosmicUserSupportContext.CTSD]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.EHP]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.ER]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.ES]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.ESOC]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.EV]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.JETS_II]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.JSC_CTX]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.NASA]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.SEAL]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.xEMU]: ALL_OG_PERMISSIONS,
        [CosmicUserSupportContext.XLABS]: ALL_OG_PERMISSIONS
    },
    userSupportContexts: OG_CONTEXTS
};
const RISK_CONTEXTS = [
    CosmicUserSupportContext.EHP,
    CosmicUserSupportContext.ESOC,
    CosmicUserSupportContext.NMC
];
const RISK_APPLICATION_CONFIG = {
    commentTypesPerCtx: {
        [CosmicUserSupportContext.ESOC]: withBaseCommentTypes(CommentType.ORB_NOTE),
        [CosmicUserSupportContext.EHP]: withBaseCommentTypes(CommentType.ORB_NOTE),
        [CosmicUserSupportContext.NMC]: withBaseCommentTypes()
    },
    documentTypesPerCtx: {
        [CosmicUserSupportContext.EHP]: [],
        [CosmicUserSupportContext.ESOC]: [],
        [CosmicUserSupportContext.NMC]: []
    },
    equipmentGroupsPerCtx: {
        [CosmicUserSupportContext.EHP]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.ESOC]: ALL_EQUIPMENT_GROUPS,
        [CosmicUserSupportContext.NMC]: ALL_EQUIPMENT_GROUPS
    },
    linkTypesPerCtx: {
        [CosmicUserSupportContext.EHP]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.ESOC]: LINK_TYPE_OTHER,
        [CosmicUserSupportContext.NMC]: LINK_TYPE_OTHER
    },
    permissionsPerCtx: {
        [CosmicUserSupportContext.EHP]: withBasePermissions(UserPermission.RISK_USER, UserPermission.RISK_ADMIN, UserPermission.ORGANIZATION_ADMIN, UserPermission.USER_ADMIN, UserPermission.USER_GROUPS_ADMIN, UserPermission.USER_PERMISSIONS),
        [CosmicUserSupportContext.ESOC]: withBasePermissions(UserPermission.RISK_USER, UserPermission.RISK_ADMIN, UserPermission.ORGANIZATION_ADMIN, UserPermission.USER_ADMIN, UserPermission.USER_GROUPS_ADMIN, UserPermission.USER_PERMISSIONS),
        [CosmicUserSupportContext.NMC]: withBasePermissions(UserPermission.RISK_USER, UserPermission.RISK_ADMIN, UserPermission.ORGANIZATION_ADMIN, UserPermission.USER_ADMIN, UserPermission.USER_GROUPS_ADMIN, UserPermission.USER_PERMISSIONS)
    },
    userSupportContexts: RISK_CONTEXTS
};
export const EsocApplicationConfigMap = {
    CDOCS: CDOCS_APPLICATION_CONFIG,
    KNOWLEDGE_BASE: KNOWLEDGE_BASE_APPLICATION_CONFIG,
    LOGISTICS: LOGISTICS_APPLICATION_CONFIG,
    OG: OG_APPLICATION_CONFIG,
    RISK: RISK_APPLICATION_CONFIG
};
